import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Alert,
  Grid,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Save as SaveIcon, 
  Cancel as CancelIcon, 
  AttachMoney,
  Warning as WarningIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { makeApiCall } from '../../config/api';

// TKT-433: price snapshots are named copies of these values. A quote can be
// priced with one instead of today's live prices.
const toSnapshotNumber = (val) => {
  if (val === '' || val === null || val === undefined) return null;
  const num = Number(val);
  return Number.isFinite(num) ? num : null;
};

const rowsToSnapshotPrices = (rows) => {
  const out = {};
  rows.forEach((row) => {
    out[row.alias] = {
      price_basic: toSnapshotNumber(row.price_basic),
      price_premium: toSnapshotNumber(row.price_premium),
      fixed_price: toSnapshotNumber(row.fixed_price)
    };
  });
  return out;
};

const todaySnapshotName = () => new Date().toLocaleDateString('en-US', {
  month: 'long', day: 'numeric', year: 'numeric'
});

const AdminSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  backgroundColor: '#f8f9fa',
  border: '2px solid #e3f2fd'
}));

const ServicePricingAdmin = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Data state
  const [prices, setPrices] = useState([]);
  
  // Edit state
  const [editingPrice, setEditingPrice] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  
  // Apply Markup State
  const [showApplyConfirm1, setShowApplyConfirm1] = useState(false);
  const [showApplyConfirm2, setShowApplyConfirm2] = useState(false);
  const [applyingMarkup, setApplyingMarkup] = useState(false);

  // Price snapshots (TKT-433). selectedSnapshotId '' = live prices.
  const { t } = useTranslation();
  const [snapshots, setSnapshots] = useState([]);
  const [selectedSnapshotId, setSelectedSnapshotId] = useState('');
  const [snapshotRows, setSnapshotRows] = useState([]);
  const [snapshotDirty, setSnapshotDirty] = useState(false);
  const [snapshotBusy, setSnapshotBusy] = useState(false);
  // { mode: 'new' (copy of the live prices) | 'copy' (copy of the snapshot
  // shown) | 'rename', name }
  const [nameDialog, setNameDialog] = useState(null);
  const [showDeleteSnapshot, setShowDeleteSnapshot] = useState(false);
  const [applyDialog, setApplyDialog] = useState(null); // { backup: bool }

  const selectedSnapshot = snapshots.find((s) => s.id === selectedSnapshotId) || null;
  const editingSnapshot = !!selectedSnapshot;
  // What the table shows: live prices, or the loaded snapshot with any
  // unsaved edits.
  const rows = editingSnapshot ? snapshotRows : prices;

  useEffect(() => {
    loadPrices();
    loadSnapshots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const flash = (message, ms = 3000) => {
    setSuccess(message);
    setTimeout(() => setSuccess(''), ms);
  };

  const loadSnapshots = async () => {
    try {
      const response = await makeApiCall('/api/admin/service-prices/snapshots');
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('servicePricing.snapshots.loadFailed', 'Could not load the price snapshots.'));
      setSnapshots(data.snapshots || []);
      return data.snapshots || [];
    } catch (err) {
      setError(err.message);
      return [];
    }
  };

  const loadSnapshot = async (id) => {
    setSnapshotBusy(true);
    try {
      const response = await makeApiCall(`/api/admin/service-prices/snapshots/${id}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('servicePricing.snapshots.loadFailed', 'Could not load the price snapshots.'));
      setSnapshotRows(data.prices || []);
      setSnapshotDirty(false);
      setSelectedSnapshotId(id);
    } catch (err) {
      setError(err.message);
    } finally {
      setSnapshotBusy(false);
    }
  };

  const confirmDiscard = () => !snapshotDirty
    || window.confirm(t('servicePricing.snapshots.discardConfirm', 'You have unsaved changes to this snapshot. Discard them?'));

  const handleSnapshotSelect = (value) => {
    if (!confirmDiscard()) return;
    if (!value) {
      setSelectedSnapshotId('');
      setSnapshotRows([]);
      setSnapshotDirty(false);
      return;
    }
    loadSnapshot(Number(value));
  };

  const saveSnapshot = async () => {
    if (!selectedSnapshot) return;
    setSnapshotBusy(true);
    try {
      const response = await makeApiCall(`/api/admin/service-prices/snapshots/${selectedSnapshot.id}`, {
        method: 'PUT',
        body: JSON.stringify({ prices: rowsToSnapshotPrices(snapshotRows) })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('servicePricing.snapshots.saveFailed', 'Could not save the snapshot.'));
      setSnapshotDirty(false);
      await loadSnapshots();
      flash(t('servicePricing.snapshots.saved', 'Snapshot "{{name}}" saved.', { name: selectedSnapshot.name }));
    } catch (err) {
      setError(err.message);
    } finally {
      setSnapshotBusy(false);
    }
  };

  const submitNameDialog = async () => {
    const name = (nameDialog?.name || '').trim();
    if (!name) {
      setError(t('servicePricing.snapshots.nameRequired', 'Enter a name for the snapshot.'));
      return;
    }
    setSnapshotBusy(true);
    try {
      if (nameDialog.mode === 'rename') {
        const response = await makeApiCall(`/api/admin/service-prices/snapshots/${selectedSnapshot.id}`, {
          method: 'PUT',
          body: JSON.stringify({ name })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || t('servicePricing.snapshots.saveFailed', 'Could not save the snapshot.'));
        await loadSnapshots();
        flash(t('servicePricing.snapshots.renamed', 'Snapshot renamed to "{{name}}".', { name }));
      } else {
        // 'new' starts from the live prices (the server copies them);
        // 'copy' duplicates the snapshot shown, including unsaved edits.
        // Either way the new snapshot opens for editing straight away.
        const body = nameDialog.mode === 'copy'
          ? { name, prices: rowsToSnapshotPrices(snapshotRows) }
          : { name };
        if (nameDialog.mode === 'new' && !confirmDiscard()) {
          setSnapshotBusy(false);
          return;
        }
        const response = await makeApiCall('/api/admin/service-prices/snapshots', {
          method: 'POST',
          body: JSON.stringify(body)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || t('servicePricing.snapshots.saveFailed', 'Could not save the snapshot.'));
        await loadSnapshots();
        await loadSnapshot(data.snapshot.id);
        flash(t('servicePricing.snapshots.created', 'Snapshot "{{name}}" saved.', { name }));
      }
      setNameDialog(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setSnapshotBusy(false);
    }
  };

  // Replace the live prices with the saved snapshot (optionally saving the
  // live prices as a backup snapshot first).
  const applySnapshotToLive = async () => {
    if (!selectedSnapshot || snapshotDirty) return;
    setSnapshotBusy(true);
    try {
      const response = await makeApiCall(`/api/admin/service-prices/snapshots/${selectedSnapshot.id}/apply`, {
        method: 'POST',
        body: JSON.stringify({ backup: !!applyDialog?.backup })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('servicePricing.snapshots.applyFailed', 'Could not apply the snapshot to the live prices.'));
      const name = selectedSnapshot.name;
      setApplyDialog(null);
      await loadPrices();
      await loadSnapshots();
      // Show the live prices so the result is visible.
      setSelectedSnapshotId('');
      setSnapshotRows([]);
      setSnapshotDirty(false);
      flash(data.backup
        ? t('servicePricing.snapshots.appliedWithBackup', 'The live prices now match "{{name}}". The previous live prices were saved as "{{backup}}".', { name, backup: data.backup.name })
        : t('servicePricing.snapshots.applied', 'The live prices now match "{{name}}".', { name }), 8000);
    } catch (err) {
      setApplyDialog(null);
      setError(err.message);
    } finally {
      setSnapshotBusy(false);
    }
  };

  const deleteSnapshot = async () => {
    if (!selectedSnapshot) return;
    setSnapshotBusy(true);
    try {
      const response = await makeApiCall(`/api/admin/service-prices/snapshots/${selectedSnapshot.id}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(response.status === 409
          ? t('servicePricing.snapshots.inUse', '{{count}} quote(s) are priced with this snapshot, so it can\'t be deleted.', { count: data.quote_count || 0 })
          : (data.error || t('servicePricing.snapshots.deleteFailed', 'Could not delete the snapshot.')));
      }
      const name = selectedSnapshot.name;
      setShowDeleteSnapshot(false);
      setSelectedSnapshotId('');
      setSnapshotRows([]);
      setSnapshotDirty(false);
      await loadSnapshots();
      flash(t('servicePricing.snapshots.deleted', 'Snapshot "{{name}}" deleted.', { name }));
    } catch (err) {
      setShowDeleteSnapshot(false);
      setError(err.message);
    } finally {
      setSnapshotBusy(false);
    }
  };

  const loadPrices = async () => {
    setLoading(true);
    try {
      const response = await makeApiCall('/api/admin/service-prices');
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load prices');
      setPrices(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (price) => {
    setEditingPrice(price);
    
    // For Global Multiplier, convert stored multiplier (1.03) to percentage (3)
    if (price.alias === 'GlobalPriceMultiplier') {
      const multiplier = parseFloat(price.fixed_price || 1.0);
      const percentage = Math.round((multiplier - 1) * 100);
      setEditFormData({
        price_basic: '',
        price_premium: '',
        fixed_price: percentage
      });
    } else {
      setEditFormData({
        price_basic: price.price_basic || '',
        price_premium: price.price_premium || '',
        fixed_price: price.fixed_price || ''
      });
    }
    setOpenDialog(true);
  };

  const handleSave = async () => {
    try {
      let bodyData = { ...editFormData };

      // For Global Multiplier, convert percentage (3) back to multiplier (1.03)
      if (editingPrice.alias === 'GlobalPriceMultiplier') {
        const percentage = parseFloat(editFormData.fixed_price || 0);
        const multiplier = 1 + (percentage / 100);
        bodyData.fixed_price = multiplier;
      }

      // Editing a snapshot: change it on screen only. Saved with "Save
      // Snapshot"; the live prices are not touched.
      if (editingSnapshot) {
        setSnapshotRows((prev) => prev.map((row) => (row.alias === editingPrice.alias
          ? {
            ...row,
            price_basic: toSnapshotNumber(bodyData.price_basic),
            price_premium: toSnapshotNumber(bodyData.price_premium),
            fixed_price: toSnapshotNumber(bodyData.fixed_price)
          }
          : row)));
        setSnapshotDirty(true);
        setOpenDialog(false);
        return;
      }

      const response = await makeApiCall(`/api/admin/service-prices/${editingPrice.id}`, {
        method: 'PUT',
        body: JSON.stringify(bodyData)
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update price');

      setSuccess('Price updated successfully');
      setOpenDialog(false);
      loadPrices();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleApplyMarkup = async () => {
    setApplyingMarkup(true);
    try {
      const response = await makeApiCall('/api/admin/service-prices/apply-global-markup', {
        method: 'POST'
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to apply markup');

      setSuccess('Markup applied permanently to all prices. Global markup reset to 0%.');
      setShowApplyConfirm1(false);
      setShowApplyConfirm2(false);
      loadPrices();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.message);
      setShowApplyConfirm1(false);
      setShowApplyConfirm2(false);
    } finally {
      setApplyingMarkup(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const num = parseFloat(value);
    const hasSubCentPrecision = num !== 0 && Math.round(num * 100) / 100 !== num;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: hasSubCentPrecision ? 6 : 2
    }).format(num);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 1200, margin: '0 auto', p: 3 }} data-demo-target="page-service-pricing-root">
      <Typography variant="h4" sx={{ mb: 3, color: '#000062', fontWeight: 'bold' }}>
        <AttachMoney sx={{ fontSize: 35, verticalAlign: 'middle', mr: 1 }} />
        Service Pricing Configuration
      </Typography>
      
      <Typography variant="body1" sx={{ mb: 4, color: '#666' }}>
        Manage base hourly rates, fixed costs, and calculation constants. These values are used in new quotes.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* TKT-433: price snapshots. The picker says which prices the table
          below edits: the live prices, or one snapshot. */}
      <AdminSection>
        <Typography variant="h6" color="primary" gutterBottom>
          {t('servicePricing.snapshots.title', 'Price Snapshots')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t('servicePricing.snapshots.intro', 'A snapshot is a saved price list with a name, for example "May 2025". A quote can be priced with a snapshot instead of the live prices. Quotes with no snapshot picked use the live prices.')}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 300 } }}>
            <InputLabel id="price-snapshot-select-label" shrink>
              {t('servicePricing.snapshots.editing', 'Editing')}
            </InputLabel>
            <Select
              labelId="price-snapshot-select-label"
              label={t('servicePricing.snapshots.editing', 'Editing')}
              value={selectedSnapshotId ? String(selectedSnapshotId) : ''}
              onChange={(e) => handleSnapshotSelect(e.target.value)}
              disabled={snapshotBusy}
              displayEmpty
              notched
            >
              <MenuItem value="">{t('servicePricing.snapshots.current', 'Live prices')}</MenuItem>
              {snapshots.map((snap) => (
                <MenuItem key={snap.id} value={String(snap.id)}>
                  {t('servicePricing.snapshots.snapshotOption', 'Snapshot: {{name}}', { name: snap.name })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={() => setNameDialog({ mode: 'new', name: todaySnapshotName() })}
            disabled={snapshotBusy}
          >
            {t('servicePricing.snapshots.new', 'New Snapshot')}
          </Button>
        </Box>

        {!editingSnapshot ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t('servicePricing.snapshots.liveNotice', 'You are editing the LIVE prices. Each change is saved right away and used by every quote on Current prices. To prepare a different price list without touching the live prices, click New Snapshot.')}
          </Alert>
        ) : (
          <Box sx={{ mt: 2, p: 2, bgcolor: '#fff', border: '2px solid #1976d2', borderRadius: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {t('servicePricing.snapshots.editingTitle', 'Editing snapshot: {{name}}', { name: selectedSnapshot.name })}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {t('servicePricing.snapshots.editingNotice', 'Changes you make in the table below only change this snapshot. The live prices stay as they are unless you click Apply to Live Prices.')}
              {' '}
              {t('servicePricing.snapshots.usedBy', 'Quotes priced with this snapshot: {{count}}.', { count: selectedSnapshot.quote_count || 0 })}
            </Typography>
            {snapshotDirty && (
              <Alert severity="warning" sx={{ mb: 1.5 }}>
                {t('servicePricing.snapshots.unsaved', 'You have unsaved changes. Click Save Snapshot to keep them.')}
              </Alert>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={saveSnapshot}
                disabled={snapshotBusy || !snapshotDirty}
              >
                {t('servicePricing.snapshots.save', 'Save Snapshot')}
              </Button>
              <Button
                variant="contained"
                color="warning"
                onClick={() => setApplyDialog({ backup: true })}
                disabled={snapshotBusy || snapshotDirty}
                title={snapshotDirty ? t('servicePricing.snapshots.saveBeforeApply', 'Save your changes before applying the snapshot.') : undefined}
              >
                {t('servicePricing.snapshots.apply', 'Apply to Live Prices')}
              </Button>
              <Button
                variant="outlined"
                onClick={() => setNameDialog({ mode: 'copy', name: t('servicePricing.snapshots.copyName', '{{name}} (copy)', { name: selectedSnapshot.name }) })}
                disabled={snapshotBusy}
              >
                {t('servicePricing.snapshots.saveAsCopy', 'Save as Copy')}
              </Button>
              <Button
                variant="outlined"
                onClick={() => setNameDialog({ mode: 'rename', name: selectedSnapshot.name })}
                disabled={snapshotBusy}
              >
                {t('servicePricing.snapshots.rename', 'Rename')}
              </Button>
              {snapshotDirty && (
                <Button
                  variant="outlined"
                  onClick={() => loadSnapshot(selectedSnapshot.id)}
                  disabled={snapshotBusy}
                >
                  {t('servicePricing.snapshots.discard', 'Discard Changes')}
                </Button>
              )}
              <Button
                variant="outlined"
                color="error"
                onClick={() => setShowDeleteSnapshot(true)}
                disabled={snapshotBusy}
              >
                {t('servicePricing.snapshots.delete', 'Delete')}
              </Button>
            </Box>
            {snapshotDirty && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {t('servicePricing.snapshots.saveBeforeApply', 'Save your changes before applying the snapshot.')}
              </Typography>
            )}
          </Box>
        )}
      </AdminSection>

      <AdminSection>
        {/* Global Multiplier Highlight */}
        {rows.find(p => p.alias === 'GlobalPriceMultiplier') && (
          <Box sx={{ mb: 3, p: 2, bgcolor: '#fff', border: '1px solid #ddd', borderRadius: 1 }}>
            <Grid container alignItems="center" spacing={2}>
              <Grid size={{ xs: 12, sm: 8 }}>
                <Typography variant="h6" color="primary" gutterBottom>
                  Global Price Markup
                </Typography>
                <Typography variant="body2">
                  This markup is applied to all service prices globally. Use this for annual price increases.
                  <br />
                  <strong>Current Value: </strong> 
                  {(() => {
                    const val = parseFloat(rows.find(p => p.alias === 'GlobalPriceMultiplier').fixed_price || 1.0);
                    const pct = Math.round((val - 1) * 100);
                    return `${pct}%`;
                  })()}
                  {' '}
                  (Global markup applied to all base prices)
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }} sx={{ textAlign: 'right' }}>
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={() => handleEditClick(rows.find(p => p.alias === 'GlobalPriceMultiplier'))}
                  sx={{ mr: 1 }}
                >
                  Edit Markup
                </Button>
                {/* Applies to the live prices only, so not offered while a
                    snapshot is shown. */}
                {!editingSnapshot && (
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => setShowApplyConfirm1(true)}
                  disabled={rows.find(p => p.alias === 'GlobalPriceMultiplier')?.fixed_price <= 1.0}
                >
                  Apply Permanently
                </Button>
                )}
              </Grid>
            </Grid>
          </Box>
        )}

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#000062' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Service Name / Description</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Alias</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Basic Rate</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Premium Rate</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Fixed Price / Value</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* SnowCalculationConstant is hardcoded to 60 in the pricing code
                  (legacy PricesHelper.cs does the same), so editing the row has
                  no effect; hide it rather than invite changes. */}
              {rows.filter((row) => row.alias !== 'SnowCalculationConstant').map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{row.service_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{row.description}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={row.alias} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{formatCurrency(row.price_basic)}</TableCell>
                  <TableCell>{formatCurrency(row.price_premium)}</TableCell>
                  <TableCell>{row.fixed_price !== null ? (
                    // Show as currency unless it looks like a constant (e.g. 60) or is the multiplier
                    row.alias === 'GlobalPriceMultiplier' ? row.fixed_price :
                    row.alias.includes('Constant') || (row.alias.includes('MinVal') && row.fixed_price > 1000) ? 
                    row.fixed_price : formatCurrency(row.fixed_price)
                  ) : '-'}</TableCell>
                  <TableCell align="right">
                    <IconButton color="primary" onClick={() => handleEditClick(row)}>
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </AdminSection>

      {/* Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Edit Price: {editingPrice?.alias === 'GlobalPriceMultiplier' ? 'Global Price Markup' : editingPrice?.service_name}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 300 }}>
            {editingPrice?.alias === 'GlobalPriceMultiplier' ? (
              <TextField
                label="Global Markup Percentage"
                type="number"
                fullWidth
                value={editFormData.fixed_price}
                onChange={(e) => setEditFormData({ ...editFormData, fixed_price: e.target.value })}
                helperText="Enter percentage increase (e.g. 3 for 3%). Use 0 for no markup."
                InputProps={{
                  endAdornment: <Typography variant="body2" sx={{ color: 'text.secondary' }}>%</Typography>
                }}
              />
            ) : (
              <>
                <TextField
                  label="Basic Rate / Price"
                  type="number"
                  fullWidth
                  value={editFormData.price_basic}
                  onChange={(e) => setEditFormData({ ...editFormData, price_basic: e.target.value })}
                  helperText="For hourly rates or standard service price"
                />
                <TextField
                  label="Premium Rate"
                  type="number"
                  fullWidth
                  value={editFormData.price_premium}
                  onChange={(e) => setEditFormData({ ...editFormData, price_premium: e.target.value })}
                  helperText="For premium service tier (if applicable)"
                />
                <TextField
                  label="Fixed Price / Constant"
                  type="number"
                  fullWidth
                  value={editFormData.fixed_price}
                  onChange={(e) => setEditFormData({ ...editFormData, fixed_price: e.target.value })}
                  helperText="For flat fees or calculation constants"
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} startIcon={<CancelIcon />}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" startIcon={<SaveIcon />}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* TKT-433: name a new snapshot / rename one */}
      <Dialog open={!!nameDialog} onClose={() => setNameDialog(null)}>
        <DialogTitle>
          {nameDialog?.mode === 'rename' && t('servicePricing.snapshots.renameTitle', 'Rename Snapshot')}
          {nameDialog?.mode === 'copy' && t('servicePricing.snapshots.copyTitle', 'Save as Copy')}
          {nameDialog?.mode === 'new' && t('servicePricing.snapshots.newTitle', 'New Snapshot')}
        </DialogTitle>
        <DialogContent>
          {nameDialog?.mode === 'new' && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              {t('servicePricing.snapshots.newHelp', 'The new snapshot starts as a copy of the live prices. It opens straight away so you can change its prices. The live prices are not changed.')}
            </Typography>
          )}
          {nameDialog?.mode === 'copy' && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              {t('servicePricing.snapshots.copyHelp', 'Saves a copy of the "{{name}}" snapshot, including any unsaved changes, under a new name.', { name: selectedSnapshot?.name })}
            </Typography>
          )}
          <TextField
            autoFocus
            fullWidth
            label={t('servicePricing.snapshots.name', 'Snapshot name')}
            value={nameDialog?.name || ''}
            onChange={(e) => setNameDialog((prev) => ({ ...prev, name: e.target.value }))}
            onKeyDown={(e) => { if (e.key === 'Enter') submitNameDialog(); }}
            inputProps={{ maxLength: 100 }}
            sx={{ mt: 1, minWidth: 320 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNameDialog(null)} startIcon={<CancelIcon />}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={submitNameDialog} variant="contained" startIcon={<SaveIcon />} disabled={snapshotBusy}>
            {t('servicePricing.snapshots.saveName', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* TKT-433: replace the live prices with a snapshot */}
      <Dialog open={!!applyDialog} onClose={() => setApplyDialog(null)}>
        <DialogTitle sx={{ color: '#d32f2f', display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon /> {t('servicePricing.snapshots.applyTitle', 'Apply to Live Prices?')}
        </DialogTitle>
        <DialogContent>
          <Typography paragraph>
            {t('servicePricing.snapshots.applyBody', 'This replaces ALL the live prices with the prices in the "{{name}}" snapshot.', { name: selectedSnapshot?.name })}
          </Typography>
          <Typography paragraph>
            {t('servicePricing.snapshots.applyEffect', 'From now on, new quotes and quotes on Current prices will use these prices. Quotes priced with a snapshot are not affected.')}
          </Typography>
          <FormControlLabel
            control={(
              <Checkbox
                checked={!!applyDialog?.backup}
                onChange={(e) => setApplyDialog((prev) => ({ ...prev, backup: e.target.checked }))}
              />
            )}
            label={t('servicePricing.snapshots.applyBackup', 'First save the current live prices as a snapshot, so this can be undone')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyDialog(null)}>{t('common.cancel', 'Cancel')}</Button>
          <Button onClick={applySnapshotToLive} variant="contained" color="error" disabled={snapshotBusy}>
            {t('servicePricing.snapshots.applyConfirm', 'Yes, Apply to Live Prices')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* TKT-433: delete a snapshot */}
      <Dialog open={showDeleteSnapshot} onClose={() => setShowDeleteSnapshot(false)}>
        <DialogTitle>{t('servicePricing.snapshots.deleteTitle', 'Delete Snapshot')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('servicePricing.snapshots.deleteConfirm', 'Delete the "{{name}}" snapshot? This can\'t be undone. Snapshots that quotes are priced with can\'t be deleted.', { name: selectedSnapshot?.name })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteSnapshot(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button onClick={deleteSnapshot} variant="contained" color="error" disabled={snapshotBusy}>
            {t('servicePricing.snapshots.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Apply Markup Confirmation Dialog 1 */}
      <Dialog open={showApplyConfirm1} onClose={() => setShowApplyConfirm1(false)}>
        <DialogTitle sx={{ color: '#d32f2f', display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon /> Warning: Permanent Price Change
        </DialogTitle>
        <DialogContent>
          <Typography paragraph>
            You are about to permanently increase ALL base service prices by the current global markup percentage.
          </Typography>
          <Typography paragraph sx={{ fontWeight: 'bold' }}>
            This action cannot be undone easily.
          </Typography>
          <Typography paragraph>
            The current global markup will be reset to 0% (1.0 multiplier) after this operation.
            All future quotes will use these new, higher base prices.
          </Typography>
          <Typography>
            Are you sure you want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowApplyConfirm1(false)}>Cancel</Button>
          <Button onClick={() => { setShowApplyConfirm1(false); setShowApplyConfirm2(true); }} variant="contained" color="error">
            Yes, Proceed
          </Button>
        </DialogActions>
      </Dialog>

      {/* Apply Markup Confirmation Dialog 2 */}
      <Dialog open={showApplyConfirm2} onClose={() => setShowApplyConfirm2(false)}>
        <DialogTitle sx={{ color: '#d32f2f', display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon /> ABSOLUTELY SURE?
        </DialogTitle>
        <DialogContent>
          <Typography paragraph>
            Please confirm one last time.
          </Typography>
          <Typography paragraph>
            This will update every single service price in the database. 
            Make sure you have verified the current markup percentage is correct before applying it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowApplyConfirm2(false)}>Cancel</Button>
          <Button 
            onClick={handleApplyMarkup} 
            variant="contained" 
            color="error" 
            disabled={applyingMarkup}
          >
            {applyingMarkup ? 'Applying...' : 'Yes, Apply Markup Permanently'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ServicePricingAdmin;

